import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  CalendarClock,
  RefreshCw,
  Pause,
  Play,
  RotateCcw,
  FileText,
  Clock,
  BarChart3,
} from 'lucide-react';
import {
  formatCountdown,
  formatInTimezone,
  type UseScheduledCampaignsResult,
} from '../hooks/useScheduledCampaigns';
import type { ScheduledCampaign, ScheduledCampaignStatus } from '../types/bulk-email-scheduling';

const STATUS_VARIANT: Record<
  ScheduledCampaignStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  pending: 'outline',
  queued: 'secondary',
  processing: 'default',
  completed: 'default',
  failed: 'destructive',
  cancelled: 'outline',
  partially_completed: 'secondary',
  paused: 'outline',
};

function CampaignCountdown({ scheduledAt, status }: { scheduledAt: string; status: string }) {
  const [label, setLabel] = useState(() => formatCountdown(scheduledAt));

  useEffect(() => {
    if (!['pending', 'queued'].includes(status)) return;
    const tick = () => setLabel(formatCountdown(scheduledAt));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scheduledAt, status]);

  if (!['pending', 'queued'].includes(status)) return null;
  return (
    <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
      <Clock className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

export type ScheduledCampaignsPanelProps = UseScheduledCampaignsResult;

export const ScheduledCampaignsPanel: React.FC<ScheduledCampaignsPanelProps> = ({
  campaigns,
  loading,
  fetchCampaigns,
  cancelCampaign,
  pauseCampaign,
  resumeCampaign,
  retryCampaign,
  logs,
  logsCampaignId,
  logsLoading,
  fetchLogs,
  setLogsCampaignId,
  getNextPending,
}) => {
  const next = getNextPending();

  const handleAction = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action failed');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Scheduled campaigns
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchCampaigns()}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Campaigns send automatically at the scheduled time (typically within 5 seconds).
            Progress refreshes every 10 seconds.
          </p>

          {next && (
            <div className="p-3 rounded-lg border bg-primary/5 border-primary/20">
              <p className="text-sm font-semibold text-foreground">Next scheduled send</p>
              <p className="text-sm text-muted-foreground mt-1">
                {next.campaign_name || next.subject} —{' '}
                {formatInTimezone(next.scheduled_at, next.timezone || 'UTC')}
              </p>
              <CampaignCountdown scheduledAt={next.scheduled_at} status={next.status} />
            </div>
          )}

          {campaigns.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No scheduled campaigns.
            </p>
          ) : (
            <div className="space-y-3">
              {campaigns.map((c) => (
                <CampaignRow
                  key={c.id}
                  campaign={c}
                  onCancel={() => handleAction(() => cancelCampaign(c.id))}
                  onPause={() => handleAction(() => pauseCampaign(c.id))}
                  onResume={() => handleAction(() => resumeCampaign(c.id))}
                  onRetry={() => handleAction(() => retryCampaign(c.id))}
                  onLogs={() => void fetchLogs(c.id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(logsCampaignId)}
        onOpenChange={(open) => !open && setLogsCampaignId(null)}
      >
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[80dvh] overflow-y-auto sm:w-full">
          <DialogHeader>
            <DialogTitle>Campaign logs</DialogTitle>
          </DialogHeader>
          {logsLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No logs yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {logs.map((log) => (
                <li key={log.id} className="border-b pb-2">
                  <span
                    className={
                      log.level === 'error'
                        ? 'text-red-600'
                        : log.level === 'warn'
                          ? 'text-amber-600'
                          : 'text-muted-foreground'
                    }
                  >
                    [{log.level}]
                  </span>{' '}
                  {log.message}
                  <div className="text-xs text-muted-foreground">
                    {new Date(log.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

function CampaignRow({
  campaign: c,
  onCancel,
  onPause,
  onResume,
  onRetry,
  onLogs,
}: {
  campaign: ScheduledCampaign;
  onCancel: () => void;
  onPause: () => void;
  onResume: () => void;
  onRetry: () => void;
  onLogs: () => void;
}) {
  const pct = Number(c.progress_percentage ?? 0);
  const processed = c.processed_count ?? (c.successful_sends ?? 0) + (c.failed_sends ?? 0);
  const total = c.recipient_count || 1;
  const showProgress = ['processing', 'queued', 'partially_completed', 'completed'].includes(
    c.status
  );

  return (
    <div className="p-4 border rounded-lg bg-muted space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0 flex-1">
          <p className="font-semibold truncate">{c.campaign_name || c.subject}</p>
          <p className="text-sm text-muted-foreground">
            {formatInTimezone(c.scheduled_at, c.timezone || 'UTC')}
            {c.timezone && c.timezone !== 'UTC' ? ` (${c.timezone})` : ''}
            {' · '}
            {c.recipient_count} recipients
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge variant={STATUS_VARIANT[c.status]}>{c.status.replace('_', ' ')}</Badge>
            <CampaignCountdown scheduledAt={c.scheduled_at} status={c.status} />
          </div>
          {c.error_message && <p className="text-xs text-red-600 mt-1">{c.error_message}</p>}
          {(c.retry_count ?? 0) > 0 && (
            <p className="text-xs text-muted-foreground">
              Retries: {c.retry_count}/{c.max_retries ?? 3}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onLogs}>
            <FileText className="h-3.5 w-3.5 mr-1" />
            Logs
          </Button>
          {['pending', 'queued', 'processing'].includes(c.status) && (
            <Button variant="outline" size="sm" onClick={onPause}>
              <Pause className="h-3.5 w-3.5 mr-1" />
              Pause
            </Button>
          )}
          {c.status === 'paused' && (
            <Button variant="outline" size="sm" onClick={onResume}>
              <Play className="h-3.5 w-3.5 mr-1" />
              Resume
            </Button>
          )}
          {['failed', 'partially_completed'].includes(c.status) && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Retry
            </Button>
          )}
          {['pending', 'queued', 'paused'].includes(c.status) && (
            <Button variant="destructive" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>

      {showProgress && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {processed} / {total} processed
              {c.successful_sends != null && ` · ${c.successful_sends} sent`}
              {c.failed_sends != null && c.failed_sends > 0 && ` · ${c.failed_sends} failed`}
            </span>
            <span>{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" />
          {c.estimated_completion_at && c.status === 'processing' && (
            <p className="text-xs text-muted-foreground">
              Est. done {new Date(c.estimated_completion_at).toLocaleTimeString()}
            </p>
          )}
        </div>
      )}

      {c.status === 'completed' && (
        <div className="flex flex-wrap items-center gap-3 text-xs border-t pt-3 mt-1">
          <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-green-600 font-medium">
            {c.successful_sends ?? 0} sent
          </span>
          {(c.failed_sends ?? 0) > 0 && (
            <span className="text-red-600 font-medium">
              {c.failed_sends} failed
            </span>
          )}
          {(c.open_count ?? 0) > 0 && (
            <span className="text-emerald-600">
              {c.open_count} opened
            </span>
          )}
          {(c.click_count ?? 0) > 0 && (
            <span className="text-blue-600">
              {c.click_count} clicked
            </span>
          )}
          {(c.bounce_count ?? 0) > 0 && (
            <span className="text-red-600">
              {c.bounce_count} bounced
            </span>
          )}
          {(c.unsubscribe_count ?? 0) > 0 && (
            <span className="text-orange-600">
              {c.unsubscribe_count} unsubscribed
            </span>
          )}
          {c.recipient_count > 0 && (
            <span className="text-muted-foreground ml-auto">
              {Math.round(((c.successful_sends ?? 0) / c.recipient_count) * 100)}% delivery
            </span>
          )}
        </div>
      )}

      {c.status === 'partially_completed' && (
        <div className="flex items-start gap-2 text-xs border-t pt-3 mt-1 text-amber-600">
          <span className="shrink-0 mt-0.5">⚠</span>
          <span>
            Partially completed: {c.successful_sends ?? 0} sent, {c.failed_sends ?? 0} failed
            out of {c.recipient_count}. You can retry to send the remaining emails.
          </span>
        </div>
      )}
    </div>
  );
}
